import React, { useEffect, useState } from 'react';

// The trader's attachments, inside the supplier dashboard's request window.
//
// The files live in the PRIVATE request-attachments bucket, and a supplier
// session can sign nothing there (the bucket's read policies are owner-by-
// folder and admin). So the URLs come from rfq-attachment-url's routed door
// ({ request_id }): it signs only for a supplier an ADMIN routed the request to
// (request_routing source 'admin' or 'public_link' - not the open pool, not the
// trader's own target), only while the request is offerable, and only paths
// that belong to the request. Anyone else gets 403 and sees the 'gated' note.
// Added 2026-10-11 (owner): the window used to show no
// attachments at all, so a routed supplier quoted without the trader's photos.
//
// ⚠️ Nothing screens an image for contact details (CLAUDE.md security rule 6).
// The control is the admin reading the request and its files before routing.
//
// reference_image is the older PUBLIC column; shown as-is when present.

const IMAGE_RE = /\.(jpe?g|png|webp|gif|heic)$/i;

const T = {
  ar: { title: 'المرفقات', gated: 'للطلب مرفقات تظهر لك بعد اعتماد الإدارة توجيه الطلب لك.', loading: 'جارٍ تحميل المرفقات…', failed: 'تعذّر فتح المرفقات. حدّث الصفحة وحاول مرة ثانية.', file: 'ملف' },
  en: { title: 'Attachments', gated: 'This request has attachments; they appear once our team routes the request to you.', loading: 'Loading attachments…', failed: 'Could not open the attachments. Refresh and try again.', file: 'File' },
  zh: { title: '附件', gated: '该需求附有文件，平台审核并推送给您后即可查看。', loading: '正在加载附件…', failed: '无法打开附件，请刷新后重试。', file: '文件' },
};

export default function SupplierRequestAttachments({ sb, requestId, paths, referenceImage, lang = 'ar' }) {
  const t = T[lang] || T.ar;
  const isAr = lang === 'ar';
  const wanted = (Array.isArray(paths) ? paths : []).filter((p) => typeof p === 'string' && p !== '');
  const key = wanted.join('|');
  const [urls, setUrls] = useState({});
  const [state, setState] = useState(wanted.length ? 'loading' : 'done');

  useEffect(() => {
    let alive = true;
    if (!requestId || !key) { setState('done'); return undefined; }
    setState('loading');
    sb.functions.invoke('rfq-attachment-url', { body: { request_id: requestId, paths: key.split('|') } })
      .then(({ data, error }) => {
        if (!alive) return;
        // 403 = not routed to this supplier by an admin (e.g. the open pool):
        // the files are admin-gated by decision, not broken. Say so.
        if (error?.context?.status === 403) { setState('gated'); return; }
        if (error || !data?.urls) {
          console.error('[SupplierRequestAttachments] attachment URLs not minted:', error?.message || error || data);
          setState('failed');
          return;
        }
        setUrls(data.urls);
        setState('done');
      })
      .catch((e) => {
        if (!alive) return;
        console.error('[SupplierRequestAttachments] invoke threw:', e?.message || e);
        setState('failed');
      });
    return () => { alive = false; };
  }, [sb, requestId, key]);

  if (!wanted.length && !referenceImage) return null;

  const items = [
    ...(referenceImage ? [{ path: referenceImage, url: referenceImage, image: true }] : []),
    ...wanted.map((p) => ({ path: p, url: urls[p], image: IMAGE_RE.test(p) })),
  ];
  const font = isAr ? { fontFamily: 'var(--font-ar)' } : {};

  return (
    <div style={{ padding: '10px 0', borderBottom: '1px solid var(--border-subtle)' }}>
      <div style={{ fontSize: 12, color: 'var(--text-disabled)', marginBottom: 8, ...font }}>{t.title}</div>
      {state === 'loading' && <p style={{ fontSize: 12, color: 'var(--text-disabled)', margin: 0, ...font }}>{t.loading}</p>}
      {state === 'failed' && <p style={{ fontSize: 12, color: 'var(--red, #b42318)', margin: 0, ...font }}>{t.failed}</p>}
      {state === 'gated' && <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0, ...font }}>{t.gated}</p>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {items.filter((it) => it.url).map((it) => (
          <a key={it.path} href={it.url} target="_blank" rel="noopener noreferrer"
            style={{ display: 'block', width: 84, height: 84, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border-subtle)', background: 'var(--bg-subtle)' }}>
            {it.image ? (
              <img src={it.url} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            ) : (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: 11, color: 'var(--text-secondary)', textAlign: 'center', padding: 6, ...font }}>
                {t.file} · {(it.path.split('.').pop() || '').toUpperCase()}
              </span>
            )}
          </a>
        ))}
      </div>
    </div>
  );
}
